import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { alertActions } from '../auth/alertSlice';

const backendURL = process.env.REACT_APP_API_URL;

export const fetchAllUsers = createAsyncThunk(
    'admin/fetchAllUsers',
    async ({ query = '', page = 1, limit = 10, sort = 'desc' } = {}, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: query, page, limit, sort }
            };
            const { data } = await axios.get(`${backendURL}/api/admin/users`, config);
            return data;
        } catch (error) {
            const errorMessage = error.response && error.response.data.message
                ? error.response.data.message
                : error.message;

            if (error.response && error.response.status === 403) {
                dispatch(alertActions.createAlert({ message: 'Unauthorized. Admin access required.', type: 'warning' }));
            }

            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchSystemSettings = createAsyncThunk(
    'admin/fetchSettings',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${backendURL}/api/admin/settings`, config);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateUserPayment = createAsyncThunk(
    'admin/updatePayment',
    async ({ userId, isPaid }, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${backendURL}/api/admin/update-payment`, {
                userId,
                isPaid,
                expiryDate: null
            }, config);

            dispatch(alertActions.createAlert({ message: 'Payment status updated successfully', type: 'success' }));
            return { userId, isPaid };
        } catch (error) {
            dispatch(alertActions.createAlert({ message: 'Failed to update payment status', type: 'warning' }));
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const updateSystemSetting = createAsyncThunk(
    'admin/updateSetting',
    async ({ key, value }, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${backendURL}/api/admin/settings`, { key, value }, config);

            dispatch(alertActions.createAlert({ message: 'Setting updated successfully', type: 'success' }));
            return { key, value };
        } catch (error) {
            dispatch(alertActions.createAlert({ message: 'Failed to update setting', type: 'warning' }));
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const handleAdminPermission = createAsyncThunk(
    "admin/handleUserPermission",
    async ({ userId }, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
            };

            const { data } = await axios.post(
                `${backendURL}/api/admin/handle-permission`,
                { userId },
                config
            );

            dispatch(alertActions.createAlert({ message: 'Permission handled successfully', type: 'success' }));
            return data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || "An unexpected error occurred";
            dispatch(alertActions.createAlert({ message: errorMessage, type: 'warning' }));
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchUserProfiles = createAsyncThunk(
    'admin/fetchUserProfiles',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${backendURL}/api/admin/user-profiles`, config);
            return data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            if (error.response?.status === 403) {
                dispatch(alertActions.createAlert({ message: 'Unauthorized. Admin access required.', type: 'warning' }));
            }
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchAuspiciousRequests = createAsyncThunk(
    'admin/fetchAuspiciousRequests',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${backendURL}/api/admin/auspicious-requests`, config);
            return data.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            if (error.response?.status === 403) {
                dispatch(alertActions.createAlert({ message: 'Unauthorized. Admin access required.', type: 'warning' }));
            }
            return rejectWithValue(errorMessage);
        }
    }
);

export const toggleUserVisibility = createAsyncThunk(
    'admin/toggleVisibility',
    async ({ userId, isHidden }, { rejectWithValue, dispatch }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${backendURL}/api/admin/toggle-visibility`, { userId, isHidden }, config);

            dispatch(alertActions.createAlert({ message: `User ${isHidden ? 'hidden' : 'visible'} successfully`, type: 'success' }));
            return { userId, isHidden };
        } catch (error) {
            dispatch(alertActions.createAlert({ message: 'Failed to update user visibility', type: 'warning' }));
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchUserChats = createAsyncThunk(
    'admin/fetchUserChats',
    async (userId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${backendURL}/api/admin/user-chats/${userId}`, config);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const fetchChatMessages = createAsyncThunk(
    'admin/fetchChatMessages',
    async (conversationId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const { data } = await axios.get(`${backendURL}/api/admin/chat-messages/${conversationId}`, config);
            return data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);
